import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { useAuth } from "@/_core/hooks/useAuth";
import ContactImportWizard from "@/components/ContactImportWizard";
import { AddToProspectingDialog } from "@/components/Crm/AddToProspectingDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { useDebounce } from "@/hooks/useDebounce";
import { usePermissions } from "@/hooks/usePermissions";
import { MIN_SEARCH_LENGTH } from "@shared/const";
import { useState, useRef, useMemo } from "react";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { downloadCsv } from "@/lib/download";
import {
  Users, Plus, Search, Upload, Trash2, Edit2, Eye,
  Phone, Building2, MapPin, Tag, X, Download, CheckSquare,
  Square, Filter, ChevronRight, Landmark, Briefcase, User,
  FileDown, ListPlus, List, Network, ArrowLeft,
  LayoutGrid, ChevronDown, ClipboardList, AlertTriangle, MoreVertical, Send,
} from "lucide-react";

// ─── Tipos ────────────────────────────────────────────────────────────────────

type ContactType = "public_admin" | "empresa" | "cliente_final";

type ContactForm = {
  phoneNumber: string;
  name: string;
  email: string;
  contactType: ContactType;
  companyName: string;
  companyType: string;
  sector: string;
  jobTitle: string;
  city: string;
  state: string;
  notes: string;
};

const emptyForm: ContactForm = {
  phoneNumber: "", name: "", email: "",
  contactType: "public_admin",
  companyName: "", companyType: "", sector: "", jobTitle: "",
  city: "", state: "", notes: "",
};

const ORGAN_TYPES = ["Prefeitura", "Câmara Municipal", "Autarquia", "Governo Estadual", "Assembleia Legislativa", "Tribunal", "Fundação Pública", "Outro"];

const CONTACT_TYPE_LABELS: Record<ContactType, string> = {
  public_admin: "Administração Pública",
  empresa: "Empresa",
  cliente_final: "Cliente Final",
};

const CONTACT_TYPE_ICONS: Record<ContactType, any> = {
  public_admin: Landmark,
  empresa: Briefcase,
  cliente_final: User,
};

// ─── Diálogo de Form ──────────────────────────────────────────────────────────

function ContactFormDialog({
  open,
  onClose,
  editId,
  initialForm,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  editId: number | null;
  initialForm: ContactForm;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<ContactForm>(initialForm);
  const utils = trpc.useUtils();

  const createMut = trpc.contacts.create.useMutation({
    onSuccess: () => { utils.contacts.list.invalidate(); toast.success("Contato criado!"); onSaved(); onClose(); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const updateMut = trpc.contacts.update.useMutation({
    onSuccess: () => { utils.contacts.list.invalidate(); toast.success("Contato atualizado!"); onSaved(); onClose(); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  // Reset form when opening
  const prevOpen = useRef(false);
  if (open && !prevOpen.current) {
    prevOpen.current = true;
    // Use setTimeout to avoid setState during render
    setTimeout(() => setForm(initialForm), 0);
  }
  if (!open) prevOpen.current = false;

  const handleSave = () => {
    if (!form.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    // Telefone é opcional: contato de órgão que só temos por e-mail (ou só
    // pelo cargo) precisa existir no CRM e ser vinculado a uma cidade. Se
    // veio preenchido, tem que ser um número plausível.
    const digits = form.phoneNumber.replace(/[^0-9]/g, "");
    if (form.phoneNumber.trim() && digits.length < 8) {
      toast.error("Telefone parece inválido. Apague ou corrija.");
      return;
    }
    const payload = {
      ...form,
      phoneNumber: digits || null,
      email: form.email || null,
      companyName: form.companyName || null,
      companyType: form.companyType || null,
      sector: form.sector || null,
      jobTitle: form.jobTitle || null,
      city: form.city || null,
      state: form.state || null,
      notes: form.notes || null,
    };
    if (editId) {
      updateMut.mutate({ id: editId, ...payload });
    } else {
      createMut.mutate(payload);
    }
  };

  const isPublicAdmin = form.contactType === "public_admin";
  const isEmpresa = form.contactType === "empresa";
  const isPending = createMut.isPending || updateMut.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto mx-2 sm:mx-0">
        <DialogHeader>
          <DialogTitle>{editId ? "Editar Contato" : "Novo Contato"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          {/* Tipo de contato */}
          <div className="col-span-2">
            <Label className="mb-2 block">Tipo de Contato</Label>
            <div className="grid grid-cols-3 gap-2">
              {(["public_admin", "empresa", "cliente_final"] as ContactType[]).map((type) => {
                const Icon = CONTACT_TYPE_ICONS[type];
                return (
                  <button
                    key={type}
                    onClick={() => setForm(f => ({ ...f, contactType: type }))}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 text-xs font-medium transition-colors ${form.contactType === type ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}
                  >
                    <Icon className="w-4 h-4" />
                    {CONTACT_TYPE_LABELS[type]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Dados básicos */}
          <div className="col-span-2">
            <Label>Telefone (com DDI)</Label>
            <Input placeholder="5511999999999 (opcional)" value={form.phoneNumber} onChange={(e) => setForm(f => ({ ...f, phoneNumber: e.target.value }))} className="mt-1" />
          </div>
          <div className="col-span-2">
            <Label>Nome *</Label>
            <Input placeholder="Nome completo" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1" />
          </div>
          <div className="col-span-2">
            <Label>E-mail</Label>
            <Input placeholder="email@exemplo.com" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="mt-1" />
          </div>

          {/* Campos de Administração Pública */}
          {isPublicAdmin && (
            <>
              <div className="col-span-2">
                <Label>Órgão (Prefeitura, Câmara, Autarquia...)</Label>
                <Input placeholder="Ex: Prefeitura Municipal de Campinas" value={form.companyName} onChange={(e) => setForm(f => ({ ...f, companyName: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <Label>Tipo de Órgão</Label>
                <select className="w-full mt-1 px-3 py-2 border border-border rounded-md text-sm bg-background" value={form.companyType} onChange={(e) => setForm(f => ({ ...f, companyType: e.target.value }))}>
                  <option value="">Selecione...</option>
                  {ORGAN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Setor / Secretaria</Label>
                <Input placeholder="Ex: Sec. de Administração" value={form.sector} onChange={(e) => setForm(f => ({ ...f, sector: e.target.value }))} className="mt-1" />
              </div>
            </>
          )}

          {/* Campos de Empresa */}
          {isEmpresa && (
            <>
              <div className="col-span-2">
                <Label>Nome da Empresa</Label>
                <Input placeholder="Ex: Empresa XYZ Ltda" value={form.companyName} onChange={(e) => setForm(f => ({ ...f, companyName: e.target.value }))} className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label>Segmento / Ramo</Label>
                <Input placeholder="Ex: Tecnologia, Saúde, Varejo..." value={form.companyType} onChange={(e) => setForm(f => ({ ...f, companyType: e.target.value }))} className="mt-1" />
              </div>
            </>
          )}

          {/* Cargo — fora dos blocos por tipo: é da pessoa, existe nos três.
              Só o Setor/Secretaria é exclusivo de administração pública. */}
          <div className="col-span-2">
            <Label>Cargo</Label>
            <Input placeholder="Ex: Pregoeiro, Secretário, Diretor de Compras" value={form.jobTitle} onChange={(e) => setForm(f => ({ ...f, jobTitle: e.target.value }))} className="mt-1" />
          </div>

          {/* Localização */}
          <div>
            <Label>Cidade</Label>
            <Input placeholder="Cidade" value={form.city} onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} className="mt-1" />
          </div>
          <div>
            <Label>UF</Label>
            <Input placeholder="SP" maxLength={2} value={form.state} onChange={(e) => setForm(f => ({ ...f, state: e.target.value.toUpperCase() }))} className="mt-1" />
          </div>
          <div className="col-span-2">
            <Label>Observações</Label>
            <Input placeholder="Notas internas..." value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Diálogo de Bulk Tag ──────────────────────────────────────────────────────

function BulkTagDialog({
  open,
  onClose,
  selectedIds,
}: {
  open: boolean;
  onClose: () => void;
  selectedIds: number[];
}) {
  const utils = trpc.useUtils();
  const { data: tags = [] } = trpc.tags.list.useQuery();
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [mode, setMode] = useState<"add" | "remove">("add");

  const addMut = trpc.contacts.bulkAddTag.useMutation({
    onSuccess: (res) => {
      utils.contacts.list.invalidate();
      utils.contacts.getTagCounts.invalidate();
      toast.success(`Tag adicionada a ${res.added} contatos!`);
      onClose();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const removeMut = trpc.contacts.bulkRemoveTag.useMutation({
    onSuccess: (res) => {
      utils.contacts.list.invalidate();
      utils.contacts.getTagCounts.invalidate();
      toast.success(`Tag removida de ${res.removed} contatos!`);
      onClose();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const handleApply = () => {
    if (!selectedTagId) { toast.error("Selecione uma tag"); return; }
    if (mode === "add") {
      addMut.mutate({ contactIds: selectedIds, tagId: selectedTagId });
    } else {
      removeMut.mutate({ contactIds: selectedIds, tagId: selectedTagId });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Aplicar Tag em Massa</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">{selectedIds.length} contatos selecionados</p>
          <div>
            <Label className="mb-2 block">Ação</Label>
            <div className="flex gap-2">
              <Button variant={mode === "add" ? "default" : "outline"} size="sm" onClick={() => setMode("add")}>Adicionar tag</Button>
              <Button variant={mode === "remove" ? "default" : "outline"} size="sm" onClick={() => setMode("remove")}>Remover tag</Button>
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Tag</Label>
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t: any) => (
                <Badge
                  key={t.id}
                  className="cursor-pointer px-2.5 py-1 text-xs"
                  style={{
                    backgroundColor: selectedTagId === t.id ? t.color : "transparent",
                    color: selectedTagId === t.id ? "white" : t.color,
                    borderColor: t.color,
                    border: "1.5px solid",
                  }}
                  onClick={() => setSelectedTagId(selectedTagId === t.id ? null : t.id)}
                >
                  {t.name}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleApply} disabled={addMut.isPending || removeMut.isPending}>
            {addMut.isPending || removeMut.isPending ? "Aplicando..." : "Aplicar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Diálogo de Inscrição em Sequência ────────────────────────────────────────

function BulkSequenceDialog({
  open,
  onClose,
  selectedIds,
}: {
  open: boolean;
  onClose: () => void;
  selectedIds: number[];
}) {
  const utils = trpc.useUtils();
  const { data: sequences = [] } = trpc.sequences.list.useQuery();
  const [selectedSeqId, setSelectedSeqId] = useState<number | null>(null);

  const enrollMut = trpc.contacts.bulkEnrollSequence.useMutation({
    onSuccess: (res) => {
      toast.success(`${res.enrolled} contatos inscritos na sequência!`);
      onClose();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const handleApply = () => {
    if (!selectedSeqId) { toast.error("Selecione uma sequência"); return; }
    enrollMut.mutate({ contactIds: selectedIds, sequenceId: selectedSeqId });
  };

  const activeSequences = (sequences as any[]).filter((s: any) => s.status === "active");

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Inscrever em Sequência</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">{selectedIds.length} contatos selecionados</p>
          <div>
            <Label className="mb-2 block">Sequência</Label>
            {activeSequences.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma sequência ativa encontrada</p>
            ) : (
              <select
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background"
                value={selectedSeqId ?? ""}
                onChange={(e) => setSelectedSeqId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Selecione...</option>
                {activeSequences.map((s: any) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleApply} disabled={enrollMut.isPending || !selectedSeqId}>
            {enrollMut.isPending ? "Inscrevendo..." : "Inscrever"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Sidebar de Tags ──────────────────────────────────────────────────────────

function TagsSidebar({
  tags,
  tagCounts,
  filterTagId,
  onTagClick,
}: {
  tags: any[];
  tagCounts: Map<number, number>;
  filterTagId: number | null;
  onTagClick: (tagId: number | null) => void;
}) {
  // Sort tags by count (most popular first)
  const sortedTags = useMemo(() => {
    return [...tags].sort((a, b) => (tagCounts.get(b.id) ?? 0) - (tagCounts.get(a.id) ?? 0));
  }, [tags, tagCounts]);

  return (
    <div className="w-56 shrink-0 space-y-4 hidden lg:block">
      {/* Tags */}
      <Card>
        <CardHeader className="pb-2 pt-3 px-3">
          <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <Tag className="h-3.5 w-3.5" /> Tags
          </CardTitle>
        </CardHeader>
        <CardContent className="px-2 pb-3 pt-0">
          <div className="space-y-0.5">
            <button
              onClick={() => onTagClick(null)}
              className={`w-full text-left px-2 py-1.5 rounded text-sm transition-colors flex items-center justify-between ${filterTagId === null ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted/50 text-foreground"}`}
            >
              <span>Todos</span>
            </button>
            {sortedTags.map((t: any) => {
              const count = tagCounts.get(t.id) ?? 0;
              return (
                <button
                  key={t.id}
                  onClick={() => onTagClick(filterTagId === t.id ? null : t.id)}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm transition-colors flex items-center justify-between gap-2 ${filterTagId === t.id ? "bg-primary/10 font-medium" : "hover:bg-muted/50"}`}
                >
                  <span className="flex items-center gap-1.5 truncate">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                    <span className="truncate">{t.name}</span>
                  </span>
                  <span className="text-xs text-muted-foreground tabular-nums shrink-0">{count}</span>
                </button>
              );
            })}
            {tags.length === 0 && (
              <p className="text-xs text-muted-foreground px-2 py-2">Nenhuma tag criada</p>
            )}
          </div>
        </CardContent>
      </Card>

    </div>
  );
}

// ─── Drill-Down Hierárquico ───────────────────────────────────────────────────

type HierarchyLevel = "state" | "city" | "organ" | "contacts";

const LEVEL_CONFIG: Record<HierarchyLevel, { label: string; labelSingular: string; icon: any; bg: string; iconBg: string }> = {
  state:    { label: "Estados",  labelSingular: "estado",  icon: MapPin,    bg: "bg-blue-50 border-blue-200 hover:bg-blue-100 dark:bg-blue-950/30 dark:border-blue-800 dark:hover:bg-blue-950/50",     iconBg: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" },
  city:     { label: "Cidades",  labelSingular: "cidade",  icon: Building2, bg: "bg-green-50 border-green-200 hover:bg-green-100 dark:bg-green-950/30 dark:border-green-800 dark:hover:bg-green-950/50", iconBg: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" },
  organ:    { label: "Órgãos",   labelSingular: "órgão",   icon: Landmark,  bg: "bg-purple-50 border-purple-200 hover:bg-purple-100 dark:bg-purple-950/30 dark:border-purple-800 dark:hover:bg-purple-950/50", iconBg: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300" },
  contacts: { label: "Contatos", labelSingular: "contato", icon: Users,     bg: "", iconBg: "" },
};

function HierarchyView() {
  const [, navigate] = useLocation();

  const [selState, setSelState] = useState<string | null>(null);
  const [selCity, setSelCity] = useState<string | null>(null);
  const [selOrgan, setSelOrgan] = useState<string | null>(null);

  // Estado → Cidade → Órgão → Contatos (3 cliques até as pessoas)
  const currentLevel: HierarchyLevel = useMemo(() => {
    if (selOrgan) return "contacts";
    if (selCity) return "organ";
    if (selState) return "city";
    return "state";
  }, [selState, selCity, selOrgan]);

  // Hierarchy query (GROUP BY via backend)
  const hierarchyQuery = trpc.contacts.getHierarchy.useQuery(
    {
      level: currentLevel as "state" | "city" | "organ",
      state: selState ?? undefined,
      city: selCity ?? undefined,
    },
    { enabled: currentLevel !== "contacts" }
  );

  // Contacts query (when drilled to organ level → show people)
  const contactsQuery = trpc.contacts.list.useQuery(
    {
      state: selState ?? undefined,
      city: selCity ?? undefined,
      organ: selOrgan ?? undefined,
      // 500 de uma vez era o maior payload de contatos do sistema. 100 cobre
      // a esmagadora maioria dos órgãos; o total continua vindo do count.
      limit: 100,
    },
    { enabled: currentLevel === "contacts" }
  );

  const breadcrumbs = useMemo(() => {
    const items: { label: string; onClick: () => void }[] = [];
    if (selState) items.push({
      label: selState,
      onClick: () => { setSelCity(null); setSelOrgan(null); },
    });
    if (selCity) items.push({
      label: selCity,
      onClick: () => { setSelOrgan(null); },
    });
    if (selOrgan) items.push({
      label: selOrgan,
      onClick: () => {},
    });
    return items;
  }, [selState, selCity, selOrgan]);

  const handleBack = () => {
    if (selOrgan) { setSelOrgan(null); return; }
    if (selCity) { setSelCity(null); return; }
    if (selState) { setSelState(null); return; }
  };

  const handleItemClick = (value: string) => {
    // "(sem valor)" também é clicável: o backend traduz o marcador para
    // "campo NULL ou vazio" em cada nível, então dá para chegar nos contatos.
    switch (currentLevel) {
      case "state": setSelState(value); break;
      case "city": setSelCity(value); break;
      case "organ": setSelOrgan(value); break;
    }
  };

  const items = hierarchyQuery.data ?? [];
  const contacts = (contactsQuery.data?.contacts ?? []) as any[];
  const isLoading = currentLevel === "contacts" ? contactsQuery.isLoading : hierarchyQuery.isLoading;
  const totalCount = currentLevel === "contacts"
    ? contactsQuery.data?.total ?? contacts.length
    : items.reduce((sum, i) => sum + i.count, 0);

  const config = LEVEL_CONFIG[currentLevel];
  const LevelIcon = config.icon;

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 flex-wrap">
        {breadcrumbs.length > 0 && (
          <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-foreground h-8" onClick={handleBack}>
            <ArrowLeft className="h-3.5 w-3.5" /> Voltar
          </Button>
        )}
        {breadcrumbs.length > 0 && <span className="text-muted-foreground text-sm">|</span>}
        <button onClick={() => { setSelState(null); setSelCity(null); setSelOrgan(null); }} className={`text-sm ${breadcrumbs.length === 0 ? "font-semibold text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          Todos os Estados
        </button>
        {breadcrumbs.map((crumb, i) => (
          <span key={i} className="flex items-center gap-1.5 text-sm">
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            <button
              onClick={crumb.onClick}
              className={i === breadcrumbs.length - 1 ? "font-semibold text-foreground" : "text-muted-foreground hover:text-foreground"}
            >
              {crumb.label}
            </button>
          </span>
        ))}
      </div>

      {/* Resumo do nível */}
      <div className="flex items-center gap-3 p-3 bg-muted/50 border rounded-lg">
        <LevelIcon className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="text-sm">
          {isLoading ? (
            <span className="text-muted-foreground">Carregando...</span>
          ) : currentLevel === "contacts" ? (
            <span>
              <strong>{totalCount}</strong> {totalCount === 1 ? "contato" : "contatos"} em <strong>{selOrgan}</strong>
            </span>
          ) : (
            <span>
              <strong>{items.length}</strong>{" "}
              {items.length === 1 ? config.labelSingular : config.label.toLowerCase()}
              {totalCount > 0 && (
                <span className="text-muted-foreground ml-1">
                  ({totalCount} {totalCount === 1 ? "contato" : "contatos"} no total)
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      ) : currentLevel === "contacts" ? (
        /* Lista de contatos do órgão */
        <div className="space-y-2">
          {contacts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p>Nenhum contato encontrado neste órgão</p>
            </div>
          ) : (
            contacts.map((c: any) => {
              const Icon = CONTACT_TYPE_ICONS[c.contactType as ContactType] ?? User;
              return (
                <Card key={c.id} className="cursor-pointer hover:shadow-sm transition-shadow" onClick={() => navigate(`/contacts/${c.id}`)}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="p-1.5 rounded-lg bg-muted shrink-0">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{c.name}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Phone className="h-3 w-3 shrink-0" />
                            <span className={c.phoneNumber ? "font-mono" : "italic"}>
                              {c.phoneNumber || "sem telefone"}
                            </span>
                            {c.email && <span className="truncate hidden sm:inline">· {c.email}</span>}
                          </div>
                          {c.sector && (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                              <LayoutGrid className="h-3 w-3 shrink-0" />
                              <span className="truncate">{c.sector}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {c.optedOut ? (
                          <Badge variant="destructive" className="text-xs">Opt-out</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-green-700 border-green-300 bg-green-50">Ativo</Badge>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      ) : (
        /* Cards agrupados por nível */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.length === 0 ? (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              <LevelIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p>Nenhum resultado encontrado</p>
            </div>
          ) : (
            items.map(({ value, count }) => {
              const ItemIcon = config.icon;
              const isNullValue = value === "(sem valor)";
              return (
                <Card
                  key={value}
                  className={`border transition-colors cursor-pointer ${config.bg}`}
                  onClick={() => handleItemClick(value)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className={`p-2 rounded-lg shrink-0 ${config.iconBg}`}>
                          <ItemIcon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className={`font-semibold text-sm leading-tight truncate ${isNullValue ? "italic text-muted-foreground" : ""}`}>{value}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {count} {count === 1 ? "contato" : "contatos"}
                          </p>
                        </div>
                      </div>
                      <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 mt-1 rotate-[-90deg]" />
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ─── Página Principal ─────────────────────────────────────────────────────────

export default function Contacts() {
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [page, setPage] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<ContactForm>(emptyForm);
  const [showImport, setShowImport] = useState(false);
  const [filterTagId, setFilterTagId] = useState<number | null>(null);
  const [filterContactType, setFilterContactType] = useState<string>("");
  const [filterState, setFilterState] = useState<string>("");
  const [filterCity, setFilterCity] = useState<string>("");
  const [filterOrgan, setFilterOrgan] = useState<string>("");
  const [filterSector, setFilterSector] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showBulkTag, setShowBulkTag] = useState(false);
  const [showBulkSequence, setShowBulkSequence] = useState(false);
  const [showBulkProspecting, setShowBulkProspecting] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "hierarchy">("list");
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState("");
  const utils = trpc.useUtils();
  const { user } = useAuth();
  const { can } = usePermissions();

  /**
   * Filtros ativos, num objeto só. A listagem e o export LEEM daqui: enquanto
   * cada um montava o seu, o export saía com um recorte diferente do que a
   * pessoa estava vendo na tela.
   */
  const activeFilters = {
    search: debouncedSearch || undefined,
    tagId: filterTagId ?? undefined,
    contactType: filterContactType || undefined,
    state: filterState || undefined,
    city: filterCity || undefined,
    organ: filterOrgan || undefined,
    sector: filterSector || undefined,
  };

  const { data, isLoading } = trpc.contacts.list.useQuery({
    ...activeFilters,
    limit: 50,
    offset: page * 50,
  });

  const exportMut = trpc.contacts.exportContacts.useMutation({
    onSuccess: (res) => {
      downloadCsv(res.csv, res.fileName);
      toast.success(`${res.total} contatos exportados.`);
      utils.contacts.importsList.invalidate();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao exportar contatos")),
  });

  const { data: tags } = trpc.tags.list.useQuery();
  const { data: tagCountsRaw } = trpc.contacts.getTagCounts.useQuery();
  const tagCounts = useMemo(() => {
    const map = new Map<number, number>();
    (tagCountsRaw ?? []).forEach((r: any) => map.set(r.tagId, r.count));
    return map;
  }, [tagCountsRaw]);

  const deleteMut = trpc.contacts.delete.useMutation({
    onSuccess: () => { utils.contacts.list.invalidate(); utils.contacts.getTagCounts.invalidate(); toast.success("Contato removido!"); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const bulkDeleteMut = trpc.contacts.bulkDelete.useMutation({
    onSuccess: (res) => {
      utils.contacts.list.invalidate();
      utils.contacts.getTagCounts.invalidate();
      toast.success(`${res.deleted} contatos removidos!`);
      setSelectedIds([]);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const deleteAllMut = trpc.contacts.deleteAll.useMutation({
    onSuccess: (res) => {
      utils.contacts.list.invalidate();
      utils.contacts.getTagCounts.invalidate();
      toast.success(`${res.deleted} contatos excluídos com sucesso!`);
      setShowDeleteAllConfirm(false);
      setDeleteAllConfirmText("");
      setSelectedIds([]);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const isAdmin = user?.tenantRole === "admin" || user?.tenantRole === "owner";

  const handleEdit = (c: any) => {
    setEditId(c.id);
    setForm({
      phoneNumber: c.phoneNumber ?? "",
      name: c.name,
      email: c.email ?? "",
      contactType: c.contactType ?? "public_admin",
      companyName: c.companyName ?? "",
      companyType: c.companyType ?? "",
      sector: c.sector ?? "",
      jobTitle: c.jobTitle ?? "",
      city: c.city ?? "",
      state: c.state ?? "",
      notes: c.notes ?? "",
    });
    setShowForm(true);
  };

  const handleBulkDelete = () => {
    if (confirm(`Tem certeza que deseja remover ${selectedIds.length} contatos? Esta ação não pode ser desfeita.`)) {
      bulkDeleteMut.mutate({ contactIds: selectedIds });
    }
  };

  const contacts = data?.contacts ?? [];

  // O Assistente vê o recorte da tela: o que está filtrado, quantos deram e
  // uma amostra. Para o resto ele tem contacts_search.
  useContextoDeLista({
    titulo: "Lista de contatos",
    filtros: {
      busca: search || null,
      tagId: filterTagId,
      tipo: filterContactType || null,
      uf: filterState || null,
      cidade: filterCity || null,
      orgao: filterOrgan || null,
      setor: filterSector || null,
      pagina: page + 1,
    },
    total: data?.total ?? contacts.length,
    itens: contacts.map((c: any) => ({ id: c.id, nome: c.name, empresa: c.companyName ?? null, cidade: c.city ?? null, uf: c.state ?? null })),
    extra: { selecionados: selectedIds.length },
  });
  const allSelected = contacts.length > 0 && contacts.every((c: any) => selectedIds.includes(c.id));
  const someSelected = selectedIds.length > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(contacts.map((c: any) => c.id));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const hasFilters = !!(filterContactType || filterState || filterCity || filterOrgan || filterSector || filterTagId);

  const handleTagClick = (tagId: number | null) => {
    setFilterTagId(tagId);
    setPage(0);
    setSelectedIds([]);
  };

  if (showImport) {
    return (
      <DashboardLayout>
        <ContactImportWizard
          onClose={() => setShowImport(false)}
          onImportDone={() => { utils.contacts.list.invalidate(); utils.contacts.getTagCounts.invalidate(); }}
        />
      </DashboardLayout>
    );
  }

  return (
    <PermissionGuard permission="contacts:view">
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Contatos</h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              {data?.total ?? 0} contatos cadastrados
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {/* Toggle de visualização */}
            <div className="flex border rounded-md overflow-hidden mr-1">
              <button
                onClick={() => setViewMode("list")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === "list" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted text-muted-foreground"}`}
              >
                <List className="h-3.5 w-3.5" /> Lista
              </button>
              <button
                onClick={() => setViewMode("hierarchy")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === "hierarchy" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted text-muted-foreground"}`}
              >
                <Network className="h-3.5 w-3.5" /> Hierarquia
              </button>
            </div>
            {viewMode === "list" && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportMut.mutate(activeFilters)}
                  disabled={exportMut.isPending || (data?.total ?? 0) === 0}
                  title="Exporta todos os contatos do filtro atual, não só esta página"
                >
                  <FileDown className="h-4 w-4 mr-1.5" />
                  {exportMut.isPending ? "Exportando..." : "Exportar CSV"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
                  <ClipboardList className="h-4 w-4 mr-1.5" /> Listas
                </Button>
                <Button size="sm" onClick={() => { setEditId(null); setForm(emptyForm); setShowForm(true); }}>
                  <Plus className="h-4 w-4 mr-1.5" /> Novo Contato
                </Button>
                {isAdmin && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Mais opções">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setShowDeleteAllConfirm(true)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir todos os contatos
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </>
            )}
          </div>
        </div>

        {/* Modo Hierarquia */}
        {viewMode === "hierarchy" && (
          <HierarchyView />
        )}

        {/* Modo Lista - Layout com Sidebar */}
        {viewMode === "list" && (
        <div className="flex gap-5 items-start">
          {/* Sidebar de Tags */}
          <TagsSidebar
            tags={tags ?? []}
            tagCounts={tagCounts}
            filterTagId={filterTagId}
            onTagClick={handleTagClick}
          />

          {/* Conteúdo Principal */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Filtros */}
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="space-y-3">
                  {/* Busca */}
                  <div className="space-y-1.5">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar por nome, telefone ou órgão..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); setSelectedIds([]); }}
                        className="pl-9"
                      />
                    </div>
                    {/* Sem este aviso, digitar 1-2 letras devolve a lista
                        inteira e parece que o filtro está quebrado. */}
                    {search.trim().length > 0 && search.trim().length < MIN_SEARCH_LENGTH && (
                      <p className="text-xs text-muted-foreground pl-1">
                        Digite ao menos {MIN_SEARCH_LENGTH} caracteres para buscar.
                      </p>
                    )}
                    {/* Busca por e-mail existe, mas em duas formas — endereço
                        inteiro ou domínio. Um trecho do que vem antes do @ não
                        casa (o endereço é criptografado), e sem este aviso o
                        resultado vazio parece filtro quebrado. */}
                    {search.trim().includes("@") && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(search.trim()) && (
                      <p className="text-xs text-muted-foreground pl-1">
                        Por e-mail, busque o endereço completo ou o domínio (ex.: @novamutum).
                      </p>
                    )}
                  </div>

                  {/* Filtros por tipo */}
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                      <Filter className="h-3 w-3" /> Tipo:
                    </span>
                    {["", "public_admin", "empresa", "cliente_final"].map((type) => (
                      <Badge
                        key={type}
                        variant={filterContactType === type ? "default" : "outline"}
                        className="cursor-pointer text-xs px-2.5 py-0.5"
                        onClick={() => { setFilterContactType(type); setPage(0); setSelectedIds([]); }}
                      >
                        {type === "" ? "Todos" : CONTACT_TYPE_LABELS[type as ContactType]}
                      </Badge>
                    ))}
                  </div>

                  {/* Filtros por Tag (inline - para mobile que não vê sidebar) */}
                  <div className="lg:hidden">
                    {(tags ?? []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 items-center">
                        <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                          <Tag className="h-3 w-3" /> Tag:
                        </span>
                        <Badge
                          variant={filterTagId === null ? "default" : "outline"}
                          className="cursor-pointer text-xs px-2 py-0.5"
                          onClick={() => { setFilterTagId(null); setPage(0); }}
                        >
                          Todos
                        </Badge>
                        {(tags ?? []).map((t: any) => (
                          <Badge
                            key={t.id}
                            variant={filterTagId === t.id ? "default" : "outline"}
                            className="cursor-pointer text-xs px-2 py-0.5"
                            style={{ borderColor: t.color, color: filterTagId === t.id ? "white" : t.color, backgroundColor: filterTagId === t.id ? t.color : "transparent" }}
                            onClick={() => { setFilterTagId(filterTagId === t.id ? null : t.id); setPage(0); }}
                          >
                            {t.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Filtros de Administração Pública */}
                  {(filterContactType === "public_admin" || filterContactType === "") && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <Input
                        placeholder="Filtrar por estado (SP)"
                        maxLength={2}
                        value={filterState}
                        onChange={(e) => { setFilterState(e.target.value.toUpperCase()); setPage(0); }}
                        className="text-sm h-8"
                      />
                      <Input
                        placeholder="Filtrar por cidade"
                        value={filterCity}
                        onChange={(e) => { setFilterCity(e.target.value); setPage(0); }}
                        className="text-sm h-8"
                      />
                      <Input
                        placeholder="Filtrar por órgão"
                        value={filterOrgan}
                        onChange={(e) => { setFilterOrgan(e.target.value); setPage(0); }}
                        className="text-sm h-8"
                      />
                      <Input
                        placeholder="Filtrar por setor"
                        value={filterSector}
                        onChange={(e) => { setFilterSector(e.target.value); setPage(0); }}
                        className="text-sm h-8"
                      />
                    </div>
                  )}

                  {/* Tag ativa na sidebar (indicador) */}
                  {filterTagId && (
                    <div className="hidden lg:flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Filtrando por tag:</span>
                      {(() => {
                        const tag = (tags ?? []).find((t: any) => t.id === filterTagId);
                        return tag ? (
                          <Badge
                            className="text-xs px-2 py-0.5"
                            style={{ backgroundColor: tag.color ?? "#3b82f6", color: "white" }}
                          >
                            {tag.name}
                            <button className="ml-1.5" onClick={() => handleTagClick(null)}>
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ) : null;
                      })()}
                    </div>
                  )}

                  {hasFilters && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => {
                      setFilterContactType("");
                      setFilterState("");
                      setFilterCity("");
                      setFilterOrgan("");
                      setFilterSector("");
                      setFilterTagId(null);
                      setPage(0);
                    }}>
                      <X className="h-3 w-3 mr-1" /> Limpar filtros
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Seleção em massa - Barra expandida */}
            {someSelected && (
              <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg text-sm flex-wrap">
                <CheckSquare className="h-4 w-4 text-primary shrink-0" />
                <span className="font-medium text-primary">
                  {selectedIds.length} selecionado{selectedIds.length > 1 ? "s" : ""} de {data?.total ?? 0}
                </span>
                <div className="flex items-center gap-1.5 ml-2">
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowBulkTag(true)}>
                    <Tag className="h-3 w-3 mr-1" /> Tag
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowBulkSequence(true)}>
                    <ListPlus className="h-3 w-3 mr-1" /> Sequência
                  </Button>
                  {can("crm:prospecting_view") && (
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowBulkProspecting(true)}>
                      <Send className="h-3 w-3 mr-1" /> Prospecção
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs text-destructive border-destructive/30 hover:bg-destructive/5"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleteMut.isPending}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    {bulkDeleteMut.isPending ? "Excluindo..." : "Excluir"}
                  </Button>
                </div>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground ml-auto" onClick={() => setSelectedIds([])}>
                  <X className="h-3 w-3 mr-1" /> Limpar
                </Button>
              </div>
            )}

            {/* Table */}
            <Card>
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
                    Carregando contatos...
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="p-12 text-center">
                    <Users className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                    <p className="text-muted-foreground font-medium">Nenhum contato encontrado</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {hasFilters ? "Tente ajustar os filtros" : "Adicione contatos manualmente ou importe um arquivo"}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="p-3 w-8">
                            <button onClick={toggleSelectAll} className="flex items-center">
                              {allSelected ? (
                                <CheckSquare className="h-4 w-4 text-primary" />
                              ) : (
                                <Square className="h-4 w-4 text-muted-foreground" />
                              )}
                            </button>
                          </th>
                          <th className="text-left p-3 font-medium text-muted-foreground">Nome</th>
                          <th className="text-left p-3 font-medium text-muted-foreground">Telefone</th>
                          <th className="text-left p-3 font-medium text-muted-foreground hidden md:table-cell">Tipo / Órgão / Empresa</th>
                          <th className="text-left p-3 font-medium text-muted-foreground hidden lg:table-cell">Setor</th>
                          <th className="text-left p-3 font-medium text-muted-foreground hidden lg:table-cell">Cidade/UF</th>
                          <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                          <th className="text-right p-3 font-medium text-muted-foreground">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map((c: any) => {
                          const Icon = CONTACT_TYPE_ICONS[c.contactType as ContactType] ?? User;
                          const isSelected = selectedIds.includes(c.id);
                          return (
                            <tr key={c.id} className={`border-b last:border-0 transition-colors ${isSelected ? "bg-primary/5" : "hover:bg-muted/20"}`}>
                              <td className="p-3">
                                <button onClick={() => toggleSelect(c.id)} className="flex items-center">
                                  {isSelected ? (
                                    <CheckSquare className="h-4 w-4 text-primary" />
                                  ) : (
                                    <Square className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </button>
                              </td>
                              <td className="p-3">
                                <div className="font-medium">{c.name}</div>
                                {c.email && <div className="text-xs text-muted-foreground">{c.email}</div>}
                              </td>
                              <td className="p-3">
                                <div className="flex items-center gap-1.5">
                                  <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                                  <span className={c.phoneNumber ? "font-mono text-xs" : "text-xs italic text-muted-foreground"}>
                                    {c.phoneNumber || "sem telefone"}
                                  </span>
                                </div>
                              </td>
                              <td className="p-3 hidden md:table-cell">
                                <div className="flex items-center gap-1.5">
                                  <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                  <div>
                                    {c.companyName && <div className="truncate max-w-[180px]">{c.companyName}</div>}
                                    {c.companyType && <div className="text-xs text-muted-foreground">{c.companyType}</div>}
                                    {!c.companyName && c.contactType && <div className="text-xs text-muted-foreground">{CONTACT_TYPE_LABELS[c.contactType as ContactType]}</div>}
                                  </div>
                                </div>
                              </td>
                              <td className="p-3 hidden lg:table-cell">
                                {c.sector && (
                                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <ChevronRight className="h-3 w-3 shrink-0" />
                                    <span className="truncate max-w-[130px]">{c.sector}</span>
                                  </div>
                                )}
                              </td>
                              <td className="p-3 hidden lg:table-cell">
                                {(c.city || c.state) && (
                                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <MapPin className="h-3.5 w-3.5" />
                                    {[c.city, c.state].filter(Boolean).join(" - ")}
                                  </div>
                                )}
                              </td>
                              <td className="p-3">
                                {c.optedOut ? (
                                  <Badge variant="destructive" className="text-xs">Opt-out</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs text-green-700 border-green-300 bg-green-50">Ativo</Badge>
                                )}
                              </td>
                              <td className="p-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button variant="ghost" size="icon" className="h-7 w-7" title="Ver detalhes" onClick={() => navigate(`/contacts/${c.id}`)}>
                                    <Eye className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(c)}>
                                    <Edit2 className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                                    onClick={() => { if (confirm("Remover este contato?")) deleteMut.mutate({ id: c.id }); }}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pagination */}
            {/* total === null significa "não calculado" (skipTotal), não zero —
                sem ele não há como paginar, então a barra some. */}
            {data && data.total !== null && data.total > 50 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Mostrando {page * 50 + 1}–{Math.min((page + 1) * 50, data.total)} de {data.total}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Anterior</Button>
                  <Button variant="outline" size="sm" disabled={(page + 1) * 50 >= data.total} onClick={() => setPage(p => p + 1)}>Próximo</Button>
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Form Dialog */}
      <ContactFormDialog
        open={showForm}
        onClose={() => { setShowForm(false); setEditId(null); setForm(emptyForm); }}
        editId={editId}
        initialForm={form}
        onSaved={() => setSelectedIds([])}
      />

      {/* Bulk Tag Dialog */}
      <BulkTagDialog
        open={showBulkTag}
        onClose={() => setShowBulkTag(false)}
        selectedIds={selectedIds}
      />

      {/* Bulk Sequence Dialog */}
      <BulkSequenceDialog
        open={showBulkSequence}
        onClose={() => setShowBulkSequence(false)}
        selectedIds={selectedIds}
      />

      <AddToProspectingDialog
        open={showBulkProspecting}
        onClose={() => setShowBulkProspecting(false)}
        onDone={() => setSelectedIds([])}
        target={selectedIds.length ? { contactIds: selectedIds } : null}
        count={selectedIds.length}
      />

      {/* Delete All Confirmation Dialog */}
      <Dialog open={showDeleteAllConfirm} onOpenChange={(o) => { if (!o) { setShowDeleteAllConfirm(false); setDeleteAllConfirmText(""); } }}>
        <DialogContent className="w-[95vw] max-w-md mx-2 sm:mx-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Excluir Todos os Contatos
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
              <strong>Atenção:</strong> Esta ação irá excluir permanentemente <strong>todos os {data?.total ?? 0} contatos</strong>, incluindo tags e campos personalizados associados. Esta ação não pode ser desfeita.
            </div>
            <div>
              <Label className="mb-2 block text-sm">
                Digite <strong>EXCLUIR TODOS</strong> para confirmar:
              </Label>
              <Input
                value={deleteAllConfirmText}
                onChange={(e) => setDeleteAllConfirmText(e.target.value)}
                placeholder="EXCLUIR TODOS"
                className="font-mono"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteAllConfirm(false); setDeleteAllConfirmText(""); }}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={deleteAllConfirmText !== "EXCLUIR TODOS" || deleteAllMut.isPending}
              onClick={() => deleteAllMut.mutate()}
            >
              {deleteAllMut.isPending ? "Excluindo..." : "Excluir Todos os Contatos"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
    </PermissionGuard>
  );
}
